import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Helmet } from 'react-helmet-async';
import { useConsentGate } from '../../hooks/use-consent-gate';
import {
  Coins, Globe, Copy, ShieldCheck, Loader2, CheckCircle2, AlertTriangle,
  LayoutDashboard, Target, BarChart3, Wallet, Sparkles, Zap,
} from 'lucide-react';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
  Publisher, PublisherEarnings, PublisherEarningsStats, PublisherPayout, WithholdingCert,
  getMyPublisher, applyPublisher, verifyPublisherDomain, getPublisherEarnings, getPublisherEarningsStats,
  savePublisherTaxProfile, getMyPayouts, getMyWithholdingCert,
} from '../../api/publisher/publisher.api';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const money = (v?: number) => `${Number(v || 0).toLocaleString('vi-VN')}đ`;
const num = (v?: number) => Number(v || 0).toLocaleString('vi-VN');
const copy = (s: string, msg = 'Đã copy') => { navigator.clipboard?.writeText(s); toast.success(msg); };
const vnd = (v?: number) => `${Number(v || 0).toLocaleString('vi-VN')} đ`;
const esc = (s?: string | null) => (s || '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));
const dvn = (iso: string) => { const d = new Date(iso); return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`; };

/** Dựng HTML chứng từ khấu trừ thuế TNCN (in được / lưu PDF). Dữ liệu thật từ server. */
function buildCertHtml(c: WithholdingCert): string {
  const row = (k: string, v: string) => `<tr><td class="k">${k}</td><td class="v">${v}</td></tr>`;
  return `<!doctype html><html lang="vi"><head><meta charset="utf-8"><title>Chứng từ khấu trừ TNCN — ${esc(c.certNo)}</title>
<style>
  *{box-sizing:border-box} body{font-family:'Times New Roman',serif;color:#111;max-width:800px;margin:24px auto;padding:0 24px;line-height:1.5}
  .top{display:flex;justify-content:space-between;font-size:13px}
  h1{text-align:center;font-size:20px;margin:18px 0 2px} .sub{text-align:center;font-size:13px;color:#444;margin-bottom:18px}
  .no{text-align:center;font-size:13px;margin-bottom:16px} .sec{font-weight:bold;margin:14px 0 6px;font-size:14px}
  table{width:100%;border-collapse:collapse;font-size:14px} td{padding:5px 6px;vertical-align:top} td.k{width:42%;color:#333} td.v{font-weight:600}
  .amt{border:1px solid #999;border-radius:6px;margin-top:8px} .amt td{border-bottom:1px solid #eee}
  .note{font-size:12px;color:#555;margin-top:16px;font-style:italic}
  .sign{display:flex;justify-content:space-between;margin-top:36px;font-size:13px;text-align:center}
  .sign div{width:45%} .sign .role{font-weight:bold} .sign .hint{color:#777;font-size:12px;margin-top:2px}
  .btn{display:block;margin:24px auto 0;padding:10px 20px;background:#0284c7;color:#fff;border:0;border-radius:8px;font-size:14px;cursor:pointer}
  @media print{.btn{display:none}}
</style></head><body>
  <div class="top"><div><b>${esc(c.payerName)}</b>${c.payerTaxCode ? `<br>MST: ${esc(c.payerTaxCode)}` : ''}${c.payerAddress ? `<br>${esc(c.payerAddress)}` : ''}</div>
    <div style="text-align:right">Mẫu tham khảo<br>03/TNCN</div></div>
  <h1>CHỨNG TỪ KHẤU TRỪ THUẾ THU NHẬP CÁ NHÂN</h1>
  <div class="sub">(Thu nhập từ hợp tác quảng cáo — Cab Ads Network)</div>
  <div class="no">Số: <b>${esc(c.certNo)}</b> — Ngày chi trả: <b>${dvn(c.paidAt)}</b></div>

  <div class="sec">I. Bên chi trả thu nhập (khấu trừ thuế)</div>
  <table>${row('Tên đơn vị', esc(c.payerName))}${row('Mã số thuế', esc(c.payerTaxCode) || '—')}${row('Địa chỉ', esc(c.payerAddress) || '—')}</table>

  <div class="sec">II. Bên nhận thu nhập</div>
  <table>
    ${row('Loại', c.isBusiness ? 'Doanh nghiệp / Hộ kinh doanh' : 'Cá nhân')}
    ${row('Họ tên / Tên đơn vị', esc(c.recipientName) || '—')}
    ${row('Mã số thuế', esc(c.recipientTaxCode) || '—')}
    ${row('Số CCCD/CMND', esc(c.recipientIdNumber) || '—')}
    ${row('Địa chỉ', esc(c.recipientAddress) || '—')}
    ${row('Website hợp tác', `${esc(c.websiteName)} (${esc(c.domain)})`)}
  </table>

  <div class="sec">III. Khoản thu nhập &amp; thuế khấu trừ</div>
  <table class="amt">
    ${row('Kỳ ghi nhận đến ngày', dvn(c.countedUpTo))}
    ${row('Tổng thu nhập chịu thuế', vnd(c.grossVnd))}
    ${row(`Thuế TNCN khấu trừ (${c.taxPercent}%)`, c.hasWithholding ? vnd(c.taxWithheldVnd) : '0 đ (không thuộc diện khấu trừ)')}
    ${row('Số tiền thực nhận', vnd(c.netVnd))}
  </table>

  <div class="note">${esc(c.legalNote)}</div>
  <div class="sign"><div><div class="role">NGƯỜI NHẬN THU NHẬP</div><div class="hint">(Ký, ghi rõ họ tên)</div></div>
    <div><div class="role">BÊN CHI TRẢ THU NHẬP</div><div class="hint">(Ký, đóng dấu)</div></div></div>
  <button class="btn" onclick="window.print()">In / Lưu PDF</button>
</body></html>`;
}

/** Thẻ KPI cho tab Tổng quan. accent=true thì làm nổi (doanh thu chính). */
const Kpi: React.FC<{ label: string; value: string; sub?: string; accent?: boolean }> = ({ label, value, sub, accent }) => (
  <div className="rounded-2xl ui-card border ui-divider p-5">
    <div className="text-sm ui-muted mb-1">{label}</div>
    <div className={`text-2xl font-extrabold ${accent ? 'text-[#0071A6]' : 'ui-text'}`}>{value}</div>
    {sub ? <div className="text-xs ui-muted mt-1.5">{sub}</div> : null}
  </div>
);

/** Placeholder cho tính năng chưa có — đánh dấu "Sắp ra mắt" để không hứa quá (đúng ý sếp: map thật + sắp ra). */
const ComingSoon: React.FC<{ title: string; desc: string }> = ({ title, desc }) => (
  <div className="rounded-2xl ui-divider border border-dashed p-8 text-center space-y-2">
    <div className="inline-flex items-center gap-1.5 rounded-full bg-[#0071A6]/10 text-[#0071A6] px-3 py-1 text-xs font-semibold">
      <Sparkles size={14} /> Sắp ra mắt
    </div>
    <div className="ui-text font-semibold">{title}</div>
    <p className="ui-muted text-sm max-w-md mx-auto">{desc}</p>
  </div>
);

const TABS = [
  { key: 'overview', label: 'Tổng quan & Hiệu suất', icon: LayoutDashboard },
  { key: 'zones', label: 'Ad Zones', icon: Target },
  { key: 'analytics', label: 'Thống kê chuyên sâu', icon: BarChart3 },
  { key: 'payout', label: 'Tài chính & Payout', icon: Wallet },
  // FE-2: ẩn tab "Giới thiệu Publisher" (còn placeholder hoàn toàn) cho gọn tới khi làm thật.
] as const;
type TabKey = typeof TABS[number]['key'];

const PublisherPage: React.FC = () => {
  const publisherGate = useConsentGate('publisher'); // bắt đồng ý điều khoản Publisher trước khi đăng ký
  const [pub, setPub] = useState<Publisher | null>(null);
  const [loading, setLoading] = useState(true);
  const [earn, setEarn] = useState<PublisherEarnings | null>(null);
  const [stats, setStats] = useState<PublisherEarningsStats | null>(null);
  const [tab, setTab] = useState<TabKey>('overview');
  const [payouts, setPayouts] = useState<PublisherPayout[]>([]);
  // Hồ sơ thuế
  const [taxBiz, setTaxBiz] = useState(false);
  const [taxName, setTaxName] = useState('');
  const [taxCode, setTaxCode] = useState('');
  const [taxId, setTaxId] = useState('');
  const [taxAddr, setTaxAddr] = useState('');
  const [savingTax, setSavingTax] = useState(false);

  // form
  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [category, setCategory] = useState('');
  const [traffic, setTraffic] = useState('');
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const loadMe = async () => {
    setLoading(true);
    try { setPub(await getMyPublisher()); } catch { /* noop */ } finally { setLoading(false); }
  };
  useEffect(() => { loadMe(); }, []);

  const isPublisher = pub && pub.status === 'Approved';
  const isPending = pub && pub.status === 'Pending';
  useEffect(() => {
    if (!isPublisher) return;
    getPublisherEarnings().then(setEarn).catch(() => undefined);
    getPublisherEarningsStats(14).then(setStats).catch(() => undefined);
    getMyPayouts().then(setPayouts).catch(() => undefined);
  }, [isPublisher]);
  // Nạp hồ sơ thuế đã lưu vào form
  useEffect(() => {
    if (!pub) return;
    setTaxBiz(!!pub.isBusiness);
    setTaxName(pub.taxLegalName || '');
    setTaxCode(pub.taxCode || '');
    setTaxId(pub.taxIdNumber || '');
    setTaxAddr(pub.taxAddress || '');
  }, [pub?.id]);

  const saveTax = async () => {
    try {
      setSavingTax(true);
      const p = await savePublisherTaxProfile({
        isBusiness: taxBiz,
        taxLegalName: taxName.trim() || undefined,
        taxCode: taxCode.trim() || undefined,
        taxIdNumber: taxId.trim() || undefined,
        taxAddress: taxAddr.trim() || undefined,
      });
      setPub(p);
      toast.success('Đã lưu hồ sơ thuế.');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Lưu hồ sơ thuế thất bại.');
    } finally { setSavingTax(false); }
  };

  // Mở chứng từ khấu trừ TNCN dạng in được (cửa sổ mới → in/lưu PDF).
  const printCert = async (payoutId: string) => {
    try {
      const c = await getMyWithholdingCert(payoutId);
      const html = buildCertHtml(c);
      const w = window.open('', '_blank');
      if (!w) { toast.error('Trình duyệt chặn cửa sổ — cho phép popup để tải chứng từ.'); return; }
      w.document.write(html); w.document.close();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Không lấy được chứng từ.');
    }
  };

  const register = async () => {
    if (!name.trim() || !website.trim()) { toast.error('Nhập tên website và địa chỉ.'); return; }
    try {
      setBusy(true);
      const p = await applyPublisher({
        name: name.trim(),
        websiteUrl: website.trim(),
        category: category.trim() || undefined,
        note: traffic ? `Traffic ước tính: ${traffic}` : undefined,
      });
      setPub(p);
      toast.success('Đã gửi đăng ký! Hồ sơ đang chờ Cab duyệt — sẽ có thông báo khi được duyệt.');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Đăng ký thất bại.');
    } finally { setBusy(false); }
  };

  const verify = async () => {
    try {
      setVerifying(true);
      const res = await verifyPublisherDomain();
      if (res.verified) { toast.success(res.message); await loadMe(); }
      else toast.error(res.message);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Xác minh thất bại.');
    } finally { setVerifying(false); }
  };

  const chartData = {
    labels: (stats?.daily || []).map((d) => d.date.slice(5)),
    datasets: [{ label: 'Doanh thu (đ)', data: (stats?.daily || []).map((d) => d.earnedCa), backgroundColor: '#0071A6', borderRadius: 4 }],
  };
  const chartOpts = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } };

  /* ── Khối xác minh domain (dùng ở tab Tổng quan) ── */
  const verifyBlock = pub && !pub.isDomainVerified ? (
    <div className="rounded-2xl border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-4 space-y-2">
      <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200 font-medium">
        <AlertTriangle size={18} /> Cần xác minh sở hữu website để quảng cáo bắt đầu chạy
      </div>
      <p className="text-sm text-amber-800 dark:text-amber-200">Dán thẻ sau vào trong <code>&lt;head&gt;</code> trang chủ website của bạn:</p>
      <div className="flex items-center gap-2">
        <code className="flex-1 text-xs bg-black/5 dark:bg-white/10 rounded px-2 py-1.5 break-all ui-text">{pub.verifyMetaTag}</code>
        <button onClick={() => copy(pub.verifyMetaTag || '', 'Đã copy thẻ meta')} className="rounded-md ui-divider border p-2 shrink-0"><Copy size={15} className="ui-text" /></button>
      </div>
      <button onClick={verify} disabled={verifying}
        className="rounded-lg bg-amber-600 text-white text-sm px-4 py-2 disabled:opacity-50 inline-flex items-center gap-2">
        {verifying ? <Loader2 size={16} className="animate-spin" /> : null} Tôi đã gắn — Xác minh ngay
      </button>
    </div>
  ) : (
    <div className="rounded-2xl border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 p-3 text-sm text-emerald-800 dark:text-emerald-200 inline-flex items-center gap-2">
      <CheckCircle2 size={16} /> Đã xác minh domain — quảng cáo đang hoạt động.
    </div>
  );

  /* ── Ad-tag thật (dùng ở tab Tổng quan & Ad Zones) ── */
  const adTagCard = (
    <div className="rounded-2xl ui-card border ui-divider p-5 space-y-2">
      <div className="flex items-center gap-2 ui-text font-semibold"><Globe size={16} /> Mã nhúng quảng cáo (ad-tag)</div>
      <p className="text-xs ui-muted">Dán đoạn mã này vào nơi bạn muốn hiển thị quảng cáo trên website.</p>
      <div className="flex items-start gap-2">
        <pre className="flex-1 text-xs bg-black/5 dark:bg-white/10 rounded-lg p-3 overflow-x-auto ui-text whitespace-pre-wrap break-all">{pub?.adTagSnippet}</pre>
        <button onClick={() => copy(pub?.adTagSnippet || '', 'Đã copy ad-tag')} className="rounded-md ui-divider border p-2 shrink-0"><Copy size={15} className="ui-text" /></button>
      </div>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-4">
      <Helmet><title>Kiếm tiền cùng Cab — Publisher</title></Helmet>
      <div className="flex items-center gap-2">
        <Coins className="text-[#0071A6]" />
        <h1 className="text-2xl font-bold ui-text">Kiếm tiền cùng Cab</h1>
      </div>

      {loading ? (
        <p className="ui-muted text-sm">Đang tải…</p>
      ) : isPending ? (
        /* ── Đã đăng ký, đang chờ admin duyệt ── */
        <div className="rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-5 space-y-2">
          <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200 font-semibold">
            <AlertTriangle size={18} /> Hồ sơ Publisher đang chờ duyệt
          </div>
          <p className="text-sm text-amber-800 dark:text-amber-200">
            Cảm ơn bạn đã đăng ký <b>{pub!.name}</b> ({pub!.domain}). Cab đang xét duyệt website của bạn —
            sau khi được duyệt, bạn sẽ nhận mã nhúng (ad-tag) và bắt đầu kiếm tiền. Thường mất 1–2 ngày làm việc.
          </p>
          <p className="text-sm text-amber-800 dark:text-amber-200">
            👉 Khi được duyệt, bạn sẽ nhận <b>thông báo</b> — bấm vào là mở thẳng dashboard. Hoặc quay lại đây bất cứ lúc nào
            qua menu <b>“Hợp tác cùng Cab” → Publisher</b> để xem trạng thái.
          </p>
        </div>
      ) : !isPublisher ? (
        /* ── Chưa là publisher: giới thiệu + đăng ký ── */
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <span className="inline-flex items-center gap-2 bg-[#0284c7]/10 text-[#0284c7] px-4 py-1.5 rounded-full text-sm font-semibold mb-3">
              <Globe size={15} /> Cab Publisher Network
            </span>
            <h2 className="text-2xl md:text-3xl font-extrabold ui-text tracking-tight mb-2">
              Kiếm tiền thông minh cùng hệ sinh thái Cab 🚀
            </h2>
            <p className="ui-muted text-sm md:text-base max-w-2xl mx-auto">
              Bạn sở hữu website hoặc blog? Hãy biến lưu lượng truy cập thành dòng tiền đều đặn mỗi ngày chỉ với một đoạn mã quảng cáo.
            </p>
          </div>

          {pub?.status === 'Rejected' && (
            <div className="max-w-3xl mx-auto mb-5 rounded-xl border border-rose-300 bg-rose-50 dark:bg-rose-950/30 p-3 text-sm text-rose-600 dark:text-rose-300">
              Đăng ký trước bị từ chối{pub.rejectReason ? `: ${pub.rejectReason}` : ''}. Bạn có thể chỉnh sửa và gửi lại — hồ sơ sẽ được duyệt lại.
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            {/* Trái: lợi ích */}
            <div className="lg:col-span-5 space-y-4">
              <div className="rounded-2xl ui-card border ui-divider p-5">
                <div className="flex items-center gap-2 text-[#0284c7] font-bold mb-1"><Zap size={18} /> Tích hợp siêu tốc</div>
                <p className="ui-muted text-sm">Chỉ cần dán 1 đoạn mã đơn giản, hệ thống tự động hiển thị và tối ưu hóa quảng cáo.</p>
              </div>
              <div className="rounded-2xl ui-card border ui-divider p-5">
                <div className="flex items-center gap-2 text-[#0284c7] font-bold mb-1"><Wallet size={18} /> Hoa hồng hấp dẫn</div>
                <p className="ui-muted text-sm">Chia <b>68%</b> doanh thu, ghi nhận theo mỗi lượt hiển thị (CPM) và click (CPC) hợp lệ.</p>
              </div>
              <div className="rounded-2xl ui-card border ui-divider p-5">
                <div className="flex items-center gap-2 text-[#0284c7] font-bold mb-1"><BarChart3 size={18} /> Thống kê minh bạch</div>
                <p className="ui-muted text-sm">Dashboard theo dõi số liệu real-time, minh bạch từng lượt và số dư có thể rút.</p>
              </div>
            </div>

            {/* Phải: form đăng ký */}
            <div className="lg:col-span-7 rounded-2xl ui-card border ui-divider p-6 md:p-8">
              <h3 className="text-lg font-bold ui-text mb-5 flex items-center gap-2">📝 Đăng ký nhanh Publisher</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium ui-text mb-1">Tên website / Đơn vị quản lý <span className="text-rose-500">*</span></label>
                  <input value={name} onChange={(e) => setName(e.target.value)} placeholder="VD: Blog Công Nghệ ABC"
                    className="w-full px-4 py-3 rounded-xl border ui-divider bg-transparent ui-text text-sm outline-none focus:ring-2 focus:ring-[#0284c7]/40 focus:border-[#0284c7] transition" />
                </div>
                <div>
                  <label className="block text-sm font-medium ui-text mb-1">Địa chỉ website (URL) <span className="text-rose-500">*</span></label>
                  <input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://domain.vn"
                    className="w-full px-4 py-3 rounded-xl border ui-divider bg-transparent ui-text text-sm outline-none focus:ring-2 focus:ring-[#0284c7]/40 focus:border-[#0284c7] transition" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium ui-text mb-1">Lĩnh vực website</label>
                    <input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="VD: Công nghệ, giải trí…"
                      className="w-full px-4 py-3 rounded-xl border ui-divider bg-transparent ui-text text-sm outline-none focus:ring-2 focus:ring-[#0284c7]/40 focus:border-[#0284c7] transition" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium ui-text mb-1">Lượng truy cập / tháng</label>
                    <select value={traffic} onChange={(e) => setTraffic(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border ui-divider bg-transparent ui-text text-sm outline-none focus:ring-2 focus:ring-[#0284c7]/40 focus:border-[#0284c7] transition">
                      <option value="">-- Chọn traffic ước tính --</option>
                      <option value="Dưới 10.000 lượt/tháng">Dưới 10.000 lượt/tháng</option>
                      <option value="10.000 - 50.000 lượt/tháng">10.000 - 50.000 lượt/tháng</option>
                      <option value="50.000 - 200.000 lượt/tháng">50.000 - 200.000 lượt/tháng</option>
                      <option value="Trên 200.000 lượt/tháng">Trên 200.000 lượt/tháng</option>
                    </select>
                  </div>
                </div>
                <button onClick={() => publisherGate.run(register)} disabled={busy}
                  className="w-full mt-1 bg-gradient-to-r from-[#0284c7] to-[#0369a1] hover:brightness-110 text-white font-bold py-3.5 px-6 rounded-xl shadow-lg shadow-[#0284c7]/25 transition disabled:opacity-50 inline-flex items-center justify-center gap-2">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : null} Đăng ký làm Publisher ngay →
                </button>
                <p className="text-xs text-center ui-muted">
                  Dùng chính tài khoản Cab bạn đang đăng nhập (không cần lập tài khoản mới); hồ sơ sẽ được Cab duyệt trước khi kích hoạt.
                  Bằng việc đăng ký, bạn đồng ý với <a href="/operating_regulations_and_term_of_service.html" target="_blank" rel="noreferrer" className="text-[#0284c7] underline">Điều khoản dịch vụ</a> của Cab Network.
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* ── Đã là publisher: DASHBOARD (layout tabbed theo mockup) ── */
        <div className="space-y-4">
          {/* Header: web + trạng thái */}
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl ui-card border ui-divider px-5 py-3">
            <div className="flex items-center gap-2">
              <span className="ui-text font-bold">{pub!.name}</span>
              <span className="ui-muted text-sm">· {pub!.domain}</span>
            </div>
            {pub!.isDomainVerified ? (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-800">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Đang hoạt động
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-800">
                Chờ xác minh domain
              </span>
            )}
          </div>

          <div className="md:flex md:gap-5">
            {/* Sub-nav tabs (trái) */}
            <nav className="flex md:flex-col gap-1 md:w-60 overflow-x-auto pb-1 shrink-0">
              {TABS.map((t) => {
                const Icon = t.icon;
                const active = tab === t.key;
                return (
                  <button key={t.key} onClick={() => setTab(t.key)}
                    className={`whitespace-nowrap flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-left transition ${active ? 'bg-[#0071A6] text-white font-semibold' : 'ui-muted hover:bg-black/5 dark:hover:bg-white/10'}`}>
                    <Icon size={17} /> {t.label}
                  </button>
                );
              })}
            </nav>

            {/* Nội dung tab (phải) */}
            <div className="flex-1 min-w-0 space-y-4 mt-3 md:mt-0">

              {/* TAB: Tổng quan & Hiệu suất */}
              {tab === 'overview' && (
                <div className="space-y-4">
                  {verifyBlock}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <Kpi label="Tổng đã kiếm" value={money(earn?.totalEarnedCa)} accent sub="Doanh thu tích luỹ" />
                    <Kpi label="Đến hạn chi trả" value={money(earn?.availableCa)} sub={`Cab chi trả theo kỳ sau khi giữ ${earn?.holdDays ?? 30} ngày`} />
                    <Kpi label="Đang giữ" value={money(earn?.heldCa)} sub="Chống gian lận" />
                    <Kpi label="Tỷ lệ chia" value={`${earn?.revSharePercent ?? pub!.revSharePercent}%`} sub="Doanh thu cho bạn" />
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="rounded-2xl ui-card border ui-divider p-5 space-y-3 lg:col-span-1">
                      <h2 className="ui-text font-semibold">Trạng thái kết nối</h2>
                      <div className="text-sm space-y-2">
                        <div className="flex justify-between py-1.5 border-b ui-divider"><span className="ui-muted">Xác minh domain</span>
                          <span className={pub!.isDomainVerified ? 'font-semibold text-emerald-600' : 'font-semibold text-amber-600'}>{pub!.isDomainVerified ? 'Thành công' : 'Chưa xác minh'}</span></div>
                        <div className="flex justify-between py-1.5 border-b ui-divider"><span className="ui-muted">Ad-tag</span>
                          <span className={pub!.isDomainVerified ? 'font-semibold text-emerald-600' : 'font-semibold ui-muted'}>{pub!.isDomainVerified ? 'Đang chạy ngầm' : 'Chờ xác minh'}</span></div>
                        <div className="flex justify-between py-1.5"><span className="ui-muted">Chi trả</span>
                          <span className="font-semibold text-[#0071A6]">Cab đối soát &amp; chi trả</span></div>
                      </div>
                    </div>
                    <div className="lg:col-span-2">{adTagCard}</div>
                  </div>
                  <div className="rounded-2xl ui-card border ui-divider p-5">
                    <h3 className="font-semibold ui-text mb-2">Doanh thu theo ngày (14 ngày)</h3>
                    <div className="h-56"><Bar data={chartData} options={chartOpts} /></div>
                  </div>
                </div>
              )}

              {/* TAB: Ad Zones */}
              {tab === 'zones' && (
                <div className="space-y-4">
                  <div className="rounded-2xl ui-card border ui-divider p-5">
                    <h2 className="ui-text font-semibold mb-1">Mã nhúng tổng (Global tag)</h2>
                    <p className="ui-muted text-sm mb-3">Hiện tại dùng chung 1 mã cho toàn website — dán vào nơi muốn hiển thị quảng cáo.</p>
                    {adTagCard}
                  </div>
                  <ComingSoon title="Nhiều vùng quảng cáo (Ad Zones)"
                    desc="Sắp tới bạn sẽ tạo được mã riêng cho từng vị trí (đầu bài, cột phải, giữa bài…) để đo và tối ưu CTR từng chỗ." />
                </div>
              )}

              {/* TAB: Thống kê chuyên sâu */}
              {tab === 'analytics' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <Kpi label="Tổng hiển thị" value={num(earn?.totalImpressions)} />
                    <Kpi label="Tổng click" value={num(earn?.totalClicks)} />
                    <Kpi label="Doanh thu tích luỹ" value={money(earn?.totalEarnedCa)} accent />
                    <Kpi label="Tỷ lệ chia" value={`${earn?.revSharePercent ?? pub!.revSharePercent}%`} />
                  </div>
                  <div className="rounded-2xl ui-card border ui-divider p-5">
                    <h3 className="font-semibold ui-text mb-2">Doanh thu theo ngày (14 ngày)</h3>
                    <div className="h-56"><Bar data={chartData} options={chartOpts} /></div>
                  </div>
                  <ComingSoon title="Phân tích theo thiết bị, quốc gia & CPC/eCPM"
                    desc="Sắp tới sẽ có bóc tách hiệu suất theo thiết bị (mobile/desktop), khu vực, cùng CPC/eCPM trung bình theo thời gian." />
                </div>
              )}

              {/* TAB: Tài chính & Payout */}
              {tab === 'payout' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <Kpi label="Đến hạn chi trả" value={money(earn?.availableCa)} accent sub={`Cab chi trả theo kỳ sau khi giữ ${earn?.holdDays ?? 30} ngày`} />
                    <Kpi label="Đang giữ" value={money(earn?.heldCa)} sub="Chống gian lận" />
                    <Kpi label="Đã nhận" value={money(earn?.paidCa)} />
                    <Kpi label="Tối thiểu chi trả" value={money(earn?.minPayoutCa)} />
                  </div>
                  <div className="rounded-2xl ui-card border ui-divider p-5 space-y-2">
                    <h2 className="ui-text font-semibold">Cơ chế đối soát &amp; chi trả</h2>
                    <p className="ui-muted text-sm">
                      Doanh thu ghi nhận theo mỗi lượt hợp lệ, <b>giữ {earn?.holdDays ?? 30} ngày</b> rà soát gian lận rồi mới chuyển sang
                      "có thể rút". Cab <b>đối soát &amp; chi trả</b> khi số dư khả dụng đạt tối thiểu {money(earn?.minPayoutCa)}.
                      {taxBiz
                        ? ' Doanh nghiệp/hộ KD tự xuất hóa đơn điện tử cho Cab.'
                        : ' Cá nhân bị khấu trừ thuế TNCN 10% với mỗi lần chi từ 5 triệu đồng trở lên (Luật Thuế TNCN 109/2025 & Nghị định 253/2026); dưới mức đó không khấu trừ.'}
                    </p>
                  </div>

                  {/* Hồ sơ thuế — để Cab xuất chứng từ khấu trừ + bảng kê hợp lệ */}
                  <div className="rounded-2xl ui-card border ui-divider p-5 space-y-3">
                    <div>
                      <h2 className="ui-text font-semibold">Hồ sơ thuế của bạn</h2>
                      <p className="ui-muted text-xs mt-0.5">Cần thiết để Cab xuất <b>chứng từ khấu trừ thuế TNCN</b> &amp; ghi nhận chi phí hợp lệ khi chi trả cho bạn.</p>
                    </div>
                    <div className="flex gap-4 text-sm">
                      <label className="inline-flex items-center gap-2 ui-text"><input type="radio" checked={!taxBiz} onChange={() => setTaxBiz(false)} /> Cá nhân</label>
                      <label className="inline-flex items-center gap-2 ui-text"><input type="radio" checked={taxBiz} onChange={() => setTaxBiz(true)} /> Doanh nghiệp / Hộ KD</label>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs ui-muted mb-1">{taxBiz ? 'Tên đơn vị (pháp lý)' : 'Họ và tên'}</label>
                        <input value={taxName} onChange={(e) => setTaxName(e.target.value)} placeholder={taxBiz ? 'CÔNG TY ...' : 'NGUYỄN VĂN A'}
                          className="w-full px-3 py-2 rounded-lg border ui-divider bg-transparent ui-text text-sm outline-none focus:ring-2 focus:ring-[#0284c7]/40" />
                      </div>
                      <div>
                        <label className="block text-xs ui-muted mb-1">Mã số thuế {taxBiz ? '' : '(nếu có)'}</label>
                        <input value={taxCode} onChange={(e) => setTaxCode(e.target.value)} placeholder="MST"
                          className="w-full px-3 py-2 rounded-lg border ui-divider bg-transparent ui-text text-sm outline-none focus:ring-2 focus:ring-[#0284c7]/40" />
                      </div>
                      {!taxBiz && (
                        <div>
                          <label className="block text-xs ui-muted mb-1">Số CCCD/CMND</label>
                          <input value={taxId} onChange={(e) => setTaxId(e.target.value)} placeholder="CCCD"
                            className="w-full px-3 py-2 rounded-lg border ui-divider bg-transparent ui-text text-sm outline-none focus:ring-2 focus:ring-[#0284c7]/40" />
                        </div>
                      )}
                      <div className={taxBiz ? 'md:col-span-1' : 'md:col-span-2'}>
                        <label className="block text-xs ui-muted mb-1">Địa chỉ</label>
                        <input value={taxAddr} onChange={(e) => setTaxAddr(e.target.value)} placeholder="Địa chỉ theo hồ sơ thuế"
                          className="w-full px-3 py-2 rounded-lg border ui-divider bg-transparent ui-text text-sm outline-none focus:ring-2 focus:ring-[#0284c7]/40" />
                      </div>
                    </div>
                    <button onClick={saveTax} disabled={savingTax}
                      className="rounded-lg bg-[#0284c7] text-white text-sm px-4 py-2 disabled:opacity-50 inline-flex items-center gap-2">
                      {savingTax ? <Loader2 size={15} className="animate-spin" /> : null} Lưu hồ sơ thuế
                    </button>
                  </div>

                  {/* Lịch sử chi trả + chứng từ khấu trừ */}
                  <div className="rounded-2xl ui-card border ui-divider p-5">
                    <h2 className="ui-text font-semibold mb-3">Lịch sử chi trả &amp; chứng từ</h2>
                    {payouts.length === 0 ? (
                      <p className="ui-muted text-sm">Chưa có lần chi trả nào. Khi Cab chi trả, chứng từ khấu trừ thuế TNCN sẽ hiện ở đây để bạn tải.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="ui-muted text-left border-b ui-divider">
                              <th className="py-2 pr-3 font-medium">Ngày</th>
                              <th className="py-2 pr-3 font-medium">Thu nhập</th>
                              <th className="py-2 pr-3 font-medium">Thuế 10%</th>
                              <th className="py-2 pr-3 font-medium">Thực nhận</th>
                              <th className="py-2 font-medium">Chứng từ</th>
                            </tr>
                          </thead>
                          <tbody>
                            {payouts.map((p) => (
                              <tr key={p.id} className="border-b ui-divider last:border-0">
                                <td className="py-2 pr-3 ui-text">{new Date(p.createdAt).toLocaleDateString('vi-VN')}</td>
                                <td className="py-2 pr-3 ui-text">{money(p.amountCa)}</td>
                                <td className="py-2 pr-3 ui-text">{money(p.taxWithheldCa)}</td>
                                <td className="py-2 pr-3 ui-text font-semibold">{money(p.netCa)}</td>
                                <td className="py-2">
                                  <button onClick={() => printCert(p.id)} className="text-[#0284c7] hover:underline font-medium text-xs">
                                    Chứng từ khấu trừ →
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  <ComingSoon title="Tự chọn phương thức nhận tiền (Ngân hàng / Ví MoMo)"
                    desc="Hiện Cab đối soát và chi trả thủ công theo kỳ. Sắp tới bạn sẽ tự thêm tài khoản ngân hàng/ví để nhận tiền tự động." />
                </div>
              )}

            </div>
          </div>
        </div>
      )}
      {publisherGate.modal}
    </div>
  );
};

export default PublisherPage;
